import React, { useMemo, useState, useRef, useEffect } from 'react';
import { useSelector } from "react-redux";
import useFetch from "../../hooks/useFetch";
import PDBOSHelp from "./PDBOSHelp";
import PDADHDHelp from "./PDADHDHelp";
import PDIKIGAIHelp from "./PDIKIGAIHelp";
import { sampleTodos } from "./data/sampleDailyData";
import AILightComponent from "../../components/AILightComponent";

const PDDaily = ({useSample, isActiveSub, subscriptionId, startDate, endDate}) => {
    const authState = useSelector((state) => state.authReducer);
    const [fetchData] = useFetch();
    const userId = authState?.user?._id;
    const userCode = authState?.user?.think5whyscode;

    const [todoDate, setTodoDate] = useState(new Date().toISOString().split("T")[0]);
    const normalizeDate = (d) => {
        if (d == null || d === "") return "";           // or return a sensible fallback

        const date = new Date(d);
            if (isNaN(date.getTime())) {
                console.warn("Invalid date passed to normalizeDate:", d);
                return "";                                    // prevent the crash
            }
        return date.toISOString().split("T")[0];        // "YYYY-MM-DD"
    };
    const normalizedStart = normalizeDate(startDate);
    const normalizedEnd   = normalizeDate(endDate);

    const isOutOfRange = !todoDate || !normalizedStart || !normalizedEnd || todoDate < normalizedStart || todoDate > normalizedEnd;
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [objective, setObjective] = useState('Random');
    const [tags, setTags] = useState('');
    const [showHistory, setShowHistory] = useState(false);
    const [selectedTaskId, setSelectedTaskId] = useState(null);

    const [todos, setTodos] = useState([]);
    const displayedTodos = useSample ? sampleTodos: todos;

    const MAX_DAILY_TODOS = 3; // Can come from business logic/API later
    const [todayTodoCount, setTodayTodoCount] = useState(0);

    const hasFetchedTodosRef = useRef(false);
    const [updateMessageMap, setUpdateMessageMap] = useState({});

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [dailySubmitted, setDailySubmitted] = useState(false);
    const [showWhatShouldIDo, setShowWhatShouldIDo] = useState(false);
    const [showLuckyDraw, setShowLuckyDraw] = useState(false);
    const [hasWonGift, setHasWonGift] = useState(false);
    const [giftClaimed, setGiftClaimed] = useState(false);
    const [showTagHelp, setShowTagHelp] = useState(false);
    const [showTaskInputHelp, setShowTaskInputHelp] = useState(false);    
    const [showADHDHelp, setShowADHDHelp] = useState(false);    
    const [showIKIGAIHelp, setShowIKIGAIHelp] = useState(false);    

    const [enableGenAIHelp, setEnableGenAIHelp] = useState(false);
    const toggleGenAIHelp = () => setEnableGenAIHelp((p) => !p);

    const [editingTask, setEditingTask] = useState(null);
    const [activeFilter, setActiveFilter] = useState("all"); // "all" | "backlog" | "cancelled" | "delayed"

    const [message, setMessage] = useState({ text: '', type: '' });
    const showMessage = (text, type = "success") => {
        setMessage({ text, type });
    };

    const validateTodo = () => {
        const errors = [];
        if (!todoDate) errors.push("Date is required");
        if (!title?.trim()) errors.push("Title is required");
        if (!objective) errors.push("Objective is required");
        if (!tags?.trim()) errors.push("At least one tag is required");
        return errors;
    };

    const updateTodayTodoCount = (todoList = []) => {
        const today = new Date().toISOString().split("T")[0];

        const count = todoList.filter((t) => {
            const created =
                (t.createdAt || t.date || "")
                    .toString()
                    .split("T")[0];

            return created === today;
        }).length;

        setTodayTodoCount(count);
    };

    const fetchMyTodos = async () => {
        if (useSample) return;
        if (hasFetchedTodosRef.current) return;

        try {
            const config = {
                url: "/pd/todo/my",
                method: "post",
                data: { userId, subscriptionId },
                headers: { Authorization: authState.token },
            };

            const response = await fetchData(config, { silent: true });

            if (response?.status) {
                const todoList = response.todos || [];
                setTodos(todoList);
                updateTodayTodoCount(todoList);

                hasFetchedTodosRef.current = true;
            }
        } catch (error) {
            console.error("Fetch Todos Error:", error);
            showMessage("Failed to load tasks", "error");
        }
    };

    useEffect(() => {
        if (useSample) return;
        if (userId) {
            fetchMyTodos();
        }
    }, [userId, useSample]);

    // Open edit panel
    const openEditPanel = (task) => {
        setSelectedTaskId(task._id);
        setEditingTask({
            title: task.title,
            tags: Array.isArray(task.tags) ? task.tags.join(", ") : task.tags || "",
            description: task.description || "",
            objective: task.objective || "Random",
            status: task.status,
            completedAt: task.completedAt || "",
            // todoDate: task.todoDate || "",
            isActive: task.isActive !== undefined ? task.isActive : true,
        });
    };

    const handleCreateTodo = async () => {
        if (useSample) {

            showMessage(
                "Demo Mode: Task creation disabled",
                "success"
            );

            return;
        }
        const errors = validateTodo();
        if (errors.length > 0) {
            showMessage(errors.join(" | "), "error");
            return;
        }
        if (todayTodoCount >= MAX_DAILY_TODOS) {
            showMessage(
                `🔥 Daily Quest Limit Reached! You can create only ${MAX_DAILY_TODOS} tasks today. Come back tomorrow 🚀`,
                "error"
            );
            return;
        }

        setIsSubmitting(true);

        try {
            const payload = {
                subscriptionId: subscriptionId,
                user: userId,
                userCode,
                title: title.trim(),
                description,
                objective,
                tags: tags.split(",").map(t => t.trim()).filter(Boolean),
                status: "Planned",
                todoDate: todoDate,
            };

            const response = await fetchData({
                url: "/pd/todo/createtodo",
                method: "post",
                data: payload,
                headers: { Authorization: authState.token },
            });

            if (response?.status) {
                showMessage("Task Created Successfully", "success");
                setDailySubmitted(true);
                
                setTodos(prev => [response.savedTodo, ...prev]);

                const winner = Math.random() < 0.05;
                setHasWonGift(winner);
                setShowLuckyDraw(false);
                setGiftClaimed(false);

                // Reset form
                setTodoDate(null);
                setTitle('');
                setDescription('');
                setObjective('Random');
                setTags('');
            }
        } catch (error) {
            showMessage("Failed to create task", "error");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleUpdateTodo = async (taskId) => {
        if (useSample) {
            setUpdateMessageMap(prev => ({
                ...prev,
                [taskId]: {
                    type: "success",
                    text: "Demo Mode: Update disabled"
                }
            }));
            return;
        }
        if (!editingTask) return;

        const tagsArray = editingTask.tags
            .split(",")
            .map(t => t.trim())
            .filter(Boolean);

        if (!editingTask.title?.trim()) {
            setUpdateMessageMap(prev => ({ ...prev, [taskId]: { type: "error", text: "Title cannot be empty" } }));
            return;
        }
        if (tagsArray.length === 0) {
            setUpdateMessageMap(prev => ({ ...prev, [taskId]: { type: "error", text: "At least one tag required" } }));
            return;
        }

        try {
            const response = await fetchData({
                url: `/pd/todo/updatetodo`,
                method: "post",
                data: {
                    taskId: taskId,
                    title: editingTask.title.trim(),
                    tags: tagsArray,
                    description: editingTask.description,
                    status: editingTask.status,
                    completedAt: editingTask.completedAt || null,
                    objective: editingTask.objective,        
                    isActive: editingTask.isActive,          
                },
                headers: { Authorization: authState.token },
            });

            if (response?.status) {
                setUpdateMessageMap(prev => ({
                    ...prev,
                    [taskId]: { type: "success", text: "Task updated successfully" }
                }));
                // await fetchMyTodos();
                setTodos(prev =>
                    prev.map(todo =>
                        todo._id === taskId
                            ? {
                                ...todo,
                                title: editingTask.title.trim(),
                                tags: tagsArray,
                                description: editingTask.description,
                                status: editingTask.status,
                                completedAt: editingTask.completedAt || null,
                                objective: editingTask.objective,
                                isActive: editingTask.isActive,
                            }
                            : todo
                    )
                );
                setSelectedTaskId(null);
                setEditingTask(null);
            } else {
                setUpdateMessageMap(prev => ({
                    ...prev,
                    [taskId]: { type: "error", text: "Update failed" }
                }));
            }
        } catch (error) {
            console.error(error);
            setUpdateMessageMap(prev => ({
                ...prev,
                [taskId]: { type: "error", text: "Server error while updating" }
            }));
        }
    };

    const today = new Date().toISOString().split("T")[0];
    const taskDate = editingTask?.todoDate  ? new Date(editingTask.todoDate).toISOString().split("T")[0] : today;
    return (
        <div className="space-y-6 bg-gray-900 p-6 rounded-xl text-white border-t-2 border-purple-400 mt-2">
            <style>{`
                .date-picker-custom::-webkit-calendar-picker-indicator {
                    filter: invert(1) sepia(1) saturate(5) hue-rotate(160deg);
                    cursor: pointer;
                }
            `}</style>
            {useSample && (
                <div className="space-y-6 bg-gray-700 p-6 rounded-xl text-white border-t-2 border-blue-600 mt-2">
                    📦 You are viewing SAMPLE DASHBOARD DATA (for demo purpose)
                </div>
            )}
            <div className="flex items-center justify-between">
                <div className='flex flex-row'>
                    <h2 className="w-fit px-2 py-1 flex items-start justify-start bg-gray-700 text-yellow-200 rounded-sm text-xs">
                        ⚡ DAILY CHECK-IN
                    </h2>
                    <span>⬇️</span>
                </div>
                <button
                    onClick={() => {
                        setShowHistory(!showHistory);
                        if (!hasFetchedTodosRef.current) {
                            fetchMyTodos();
                        }
                    }}
                    className="w-fit px-1 py-1 flex items-center justify-center bg-cyan-500 hover:bg-cyan-400 text-black rounded-lg text-[10px] lg:text-xs"
                >
                    📜 View/Edit Task History
                </button>
            </div>

            {showHistory && (
                <div className="mt-3 bg-[#0f172a] border border-gray-800 rounded-xl">
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-0.5 border-b border-gray-400">
                        <button
                            onClick={() => setActiveFilter("all")}
                            className={`flex items-center gap-1.5 px-2 py-1 text-xs rounded-lg border transition-all ${
                                activeFilter === "all" 
                                    ? "bg-white/10 text-white border-gray-400" 
                                    : "bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 border-blue-500/30"
                            }`}
                        >
                            📋 All
                        </button>

                        <button
                            onClick={() => setActiveFilter("backlog")}
                            className={`flex items-center gap-1.5 px-2 py-1 text-xs rounded-lg border transition-all ${
                                activeFilter === "backlog" 
                                    ? "bg-blue-500/20 text-blue-400 border-blue-500" 
                                    : "bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 border-blue-500/30"
                            }`}
                        >
                            📋 Backlog <span className="text-[10px] text-gray-400">
                                ({displayedTodos.filter(t => t.status === "Planned").length})
                            </span>
                        </button>

                        <button
                            onClick={() => setActiveFilter("cancelled")}
                            className={`flex items-center gap-1.5 px-2 py-1 text-xs rounded-lg border transition-all ${
                                activeFilter === "cancelled" 
                                    ? "bg-red-500/20 text-red-400 border-red-500" 
                                    : "bg-red-500/10 hover:bg-red-500/20 text-red-400 border-red-500/30"
                            }`}
                        >
                            ❌ Cancelled <span className="text-[10px] text-gray-400">
                                ({displayedTodos.filter(t => t.status === "Cancelled").length})
                            </span>
                        </button>

                        <button
                            onClick={() => setActiveFilter("delayed")}
                            className={`flex items-center gap-1.5 px-2 py-1 text-xs rounded-lg border transition-all ${
                                activeFilter === "delayed" 
                                    ? "bg-orange-500/20 text-orange-400 border-orange-500" 
                                    : "bg-orange-500/10 hover:bg-orange-500/20 text-orange-400 border-orange-500/30"
                            }`}
                        >
                            ⏳ Delayed 
                            <span className="text-[10px] text-gray-400">
                                ({displayedTodos.filter(t => 
                                    t.status === "Done" && 
                                    t.completedAt && 
                                    new Date(t.completedAt) > new Date(t.todoDate)
                                ).length})
                            </span>
                        </button>
                    </div>
                    <div className="grid grid-cols-12 gap-2 px-3 py-1 bg-[#111827] text-xs text-teal-200 uppercase">
                        <div className="col-span-1 text-[10px] lg:text-xs">SN</div>
                        <div className="col-span-2 text-[10px] lg:text-xs">Date</div>
                        <div className="col-span-7 text-[10px] lg:text-xs">Task</div>
                        <div className="col-span-2 text-[10px] lg:text-xs">Status</div>
                    </div>

                    <div className="p-1">
                        {displayedTodos
                            .filter(item => {
                                if (activeFilter === "all") return true;
                                if (activeFilter === "backlog") return item.status === "Planned";
                                if (activeFilter === "cancelled") return item.status === "Cancelled";
                                if (activeFilter === "delayed") {
                                    return item.status === "Done" && 
                                        item.completedAt && 
                                        new Date(item.completedAt) > new Date(item.todoDate);
                                }
                                return true;
                            })
                            .slice()
                            .sort((a, b) => new Date(b.todoDate) - new Date(a.todoDate))
                            .map((item, index) => (
                                <div key={item._id} className="border-t border-gray-800 px-2 py-1">
                                    <div className="grid grid-cols-12 gap-2 items-start">
                                       <div className="col-span-1 text-[10px] lg:text-xs text-gray-200 pt-1">
                                            {index + 1}
                                        </div>

                                        <div className="col-span-2 text-[10px] lg:text-xs text-gray-300 pt-1">
                                            {new Date(item.todoDate).toLocaleDateString("en-IN")}
                                        </div>

                                        <div className="col-span-7">
                                            <div className="text-[10px] lg:text-xs font-medium text-white">
                                                {item.title}
                                            </div>

                                            <div className="flex flex-wrap gap-2 mt-2">
                                                {item.tags?.length > 0 ? (
                                                    item.tags.map((t, i) => (
                                                        <span
                                                            key={i}
                                                            className="bg-gray-700 px-2 py-1 text-[10px] lg:text-xs rounded"
                                                        >
                                                            {t}
                                                        </span>
                                                    ))
                                                ) : (
                                                    <span className="text-[10px] lg:text-xs text-gray-500">
                                                        No tags
                                                    </span>
                                                )}
                                            </div>

                                            {item.status === "Done" && item.completedAt && (
                                                <div className="text-[10px] lg:text-xs text-green-400 mt-2">
                                                    ✅ {new Date(item.completedAt).toLocaleDateString("en-IN")}
                                                </div>
                                            )}
                                        </div>

                                        {/* <div className="col-span-2 flex justify-end">
                                            <button
                                                disabled={!isActiveSub}
                                                onClick={() => openEditPanel(item)}
                                                className={`w-40 px-2 py-1 rounded text-[10px] lg:text-xs font-bold ${
                                                    item.status === "Done" ? "bg-green-500/20 text-green-400" :
                                                    item.status === "Cancelled" ? "bg-red-500/20 text-red-400" :
                                                    "bg-cyan-500/20 text-cyan-300"
                                                }`}
                                            >
                                                {item.status}
                                            </button>
                                        </div> */}
                                        <div className="col-span-2 flex justify-end">
                                            <button
                                                disabled={!isActiveSub}
                                                onClick={() => openEditPanel(item)}
                                                className={`w-40 px-2 py-1 rounded text-[10px] lg:text-xs font-bold ${
                                                    item.status === "Done" ? "bg-green-500/20 text-green-400" :
                                                    item.status === "Cancelled" ? "bg-red-500/20 text-red-400" :
                                                    "bg-cyan-500/20 text-cyan-300"
                                                }`}
                                            >
                                                {item.status}
                                                {item.status === "Done" && item.completedAt && new Date(item.completedAt) > new Date(item.todoDate) && (
                                                    <span className="text-orange-400 text-[9px] ml-1">
                                                        (+{Math.ceil((new Date(item.completedAt) - new Date(item.todoDate)) / (1000 * 60 * 60 * 24))}d)
                                                    </span>
                                                )}
                                            </button>
                                        </div>
                                    </div>

                                    {/* Edit Panel */}
                                    {selectedTaskId === item._id && editingTask && (
                                        <div className="mt-3 bg-[#0f172a] rounded-xl p-4 border-2 border-cyan-500 space-y-3">
                                            <div className="text-xs text-cyan-200 font-semibold">
                                                Update Task Status & Task Info
                                            </div>
                                            <div className="space-y-3">
                                                <div className="text-xs text-gray-200">Task Status</div>
                                                
                                                <div className="flex flex-row gap-2">
                                                    <button
                                                        onClick={() => setEditingTask(prev => ({
                                                            ...prev,
                                                            status: "Done",
                                                            completedAt: prev.completedAt || new Date().toISOString().split("T")[0]
                                                        }))}
                                                        disabled={taskDate > today}
                                                        className={`flex items-center justify-center gap-2 w-fit px-2 py-1 rounded-xl text-xs lg:text-sm font-semibold transition-all
                                                            disabled:opacity-40 disabled:cursor-not-allowed
                                                            ${editingTask.status === "Done" 
                                                                ? "bg-green-500 text-black shadow-lg shadow-green-500/30" 
                                                                : "bg-green-500/10 hover:bg-green-500/20 text-green-400 border border-green-500/30"}`}
                                                    >
                                                        ✅ Done
                                                    </button>                                                    

                                                    <button
                                                        onClick={() => setEditingTask(prev => ({
                                                            ...prev,
                                                            status: "Cancelled",
                                                            completedAt: null
                                                        }))}
                                                        className={`flex items-center justify-center gap-2 w-fit px-2 py-1 rounded-xl text-xs lg:text-sm font-semibold transition-all
                                                            ${editingTask.status === "Cancelled" 
                                                                ? "bg-red-500 text-white shadow-lg shadow-red-500/30" 
                                                                : "bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/30"}`}
                                                    >
                                                        ❌ Cancelled
                                                    </button>
                                                </div>
                                                {editingTask.status === "Done" && (
                                                    <div>
                                                        <div className="flex items-center justify-between text-xs text-gray-200 mb-1.5">
                                                            <span>Completed On</span>
                                                            <span className="text-cyan-400 text-xs">📅</span>
                                                        </div>
                                                        
                                                        <div className="relative">
                                                            <input
                                                                type="date"
                                                                value={editingTask.completedAt}
                                                                onChange={(e) => setEditingTask(prev => ({ 
                                                                    ...prev, 
                                                                    completedAt: e.target.value 
                                                                }))}
                                                                className="w-full bg-[#111827] border border-gray-600 rounded-xl p-3 text-sm text-white 
                                                                        focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 outline-none
                                                                        date-picker-custom"
                                                            />
                                                            <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-gray-200">
                                                                📆
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            <div>
                                                <div className="text-xs text-gray-200">Title</div>
                                                <input
                                                    value={editingTask.title}
                                                    onChange={(e) => setEditingTask(prev => ({ ...prev, title: e.target.value }))}
                                                    className="w-full bg-[#111827] border border-gray-700 rounded-lg p-2 text-xs text-white"
                                                />
                                            </div>

                                            <div>
                                                <div className="text-xs text-gray-200">Tags (comma separated)</div>
                                                <input
                                                    value={editingTask.tags}
                                                    onChange={(e) => setEditingTask(prev => ({ ...prev, tags: e.target.value }))}
                                                    className="w-full bg-[#111827] border border-gray-700 rounded-lg p-2 text-xs text-white"
                                                />
                                            </div>

                                            <div>
                                                <div className="text-xs text-gray-200">Note</div>
                                                <input
                                                    value={editingTask.description}
                                                    onChange={(e) => setEditingTask(prev => ({ ...prev, description: e.target.value }))}
                                                    maxLength={100}
                                                    className="w-full bg-[#111827] border border-gray-700 rounded-lg p-2 text-xs text-white"
                                                />
                                            </div>

                                            <div>
                                                <div className="text-xs text-gray-200 mb-1">Objective</div>
                                                <div className="grid grid-cols-3 lg:grid-cols-6 gap-0.5">
                                                    {[
                                                        { emoji: "🎓", label: "Learning" },
                                                        { emoji: "💼", label: "Career" },
                                                        { emoji: "🧠", label: "Discipline" },
                                                        { emoji: "💪", label: "Health" },
                                                        { emoji: "⚽", label: "Sports" },
                                                        { emoji: "🚀", label: "Build" },
                                                        { emoji: "📈", label: "Productivity" },
                                                        { emoji: "🎯", label: "Focus" },
                                                        { emoji: "🌱", label: "Growth" },

                                                        { emoji: "🧠", label: "Attention" },
                                                        { emoji: "❤️", label: "Connection" },
                                                        { emoji: "😊", label: "Emotional Regulation" },
                                                        { emoji: "📋", label: "Routine" },
                                                        { emoji: "🏃", label: "Movement" },
                                                        { emoji: "😴", label: "Sleep" },
                                                        { emoji: "🌟", label: "Confidence" },
                                                        { emoji: "🎬", label: "Entertainment" },

                                                        { emoji: "🧘", label: "Mindfulness" },
                                                        { emoji: "🎨", label: "Creativity" },
                                                        { emoji: "🤝", label: "Relationships" },
                                                        { emoji: "🚀", label: "Entrepreneurship" },
                                                        { emoji: "💰", label: "Financial" },
                                                        { emoji: "👑", label: "Leadership" },

                                                        { emoji: "🎲", label: "Random" }
                                                    ].map((obj) => (
                                                        <button
                                                            key={obj.label}
                                                            onClick={() => setEditingTask(prev => ({ ...prev, objective: obj.label }))}
                                                            className={`w-fit text-[11px] lg:text-xs px-1 py-1 rounded-lg leading-none border transition-all
                                                                ${editingTask.objective === obj.label 
                                                                    ? "bg-cyan-500 text-black border-cyan-400" 
                                                                    : "bg-[#111827] text-gray-200 border-gray-700 hover:border-gray-600"}`}
                                                        >
                                                            {obj.emoji}{obj.label}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>

                                            {/* Active Status Toggle */}
                                            <div className="flex items-center justify-between bg-[#111827] border border-gray-700 rounded-lg p-3">
                                                <div className="text-xs text-gray-200">Task is Active</div>
                                                <button
                                                    onClick={() => setEditingTask(prev => ({ ...prev, isActive: !prev.isActive }))}
                                                    className={`w-24 px-4 py-1 text-xs font-bold rounded-lg transition-all
                                                        ${editingTask.isActive 
                                                            ? "bg-green-500 text-black" 
                                                            : "bg-red-500/80 text-white"}`}
                                                >
                                                    {editingTask.isActive ? "INACTIVE?" : "ACTIVE?"}
                                                </button>
                                            </div>
                                            {updateMessageMap[item._id] && (
                                                <div className={`text-xs font-semibold px-3 py-1 rounded-lg ${
                                                    updateMessageMap[item._id].type === "success"
                                                        ? "bg-green-900/40 text-green-300 border border-green-500/30"
                                                        : "bg-red-900/40 text-red-300 border border-red-500/30"
                                                }`}>
                                                    {updateMessageMap[item._id].text}
                                                </div>
                                            )}

                                            <button
                                                onClick={() => handleUpdateTodo(item._id)}
                                                className="w-fit mt-2 px-2 py-1 rounded-lg text-xs lg:text-sm font-bold bg-cyan-500 hover:bg-cyan-400 text-black"
                                            >
                                                🔄 Update Task
                                            </button>
                                            <button
                                                onClick={() => { setEditingTask(null); setSelectedTaskId(null); }}
                                                className="w-fit px-2 py-1 rounded-lg text-xs lg:text-sm font-bold bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                                            >
                                                ✕ Close
                                            </button>
                                        </div>
                                    )}
                                </div>
                            ))}
                    </div>
                </div>
            )}

            <div className="bg-[#1f2937] rounded-xl px-2 py-1">
                <div className='flex justify-between'>
                    <p className="text-[10px] lg:text-xs text-cyan-200 font-semibold">
                        🎯 Input 1-3 key task for today
                    </p>
                    <p className="text-[10px] lg:text-xs text-gray-300">
                        💡 1-3 deep task &gt; 10 random tasks
                    </p>
                </div>
                <div className="flex items-center justify-between gap-2 text-[10px] lg:text-xs text-gray-200 mt-1 text-left bg-cyan-500/5 border border-cyan-500/10 rounded-lg py-1 px-2">
                    <div className="flex-1 text-[10px] lg:text-xs">
                        📅 Small daily wins compound into identity change
                    </div>
                    <div className='text-[10px] lg:text-xs gap-1 flex flex-row'>
                        <button
                            onClick={() => setShowWhatShouldIDo(!showWhatShouldIDo)}
                            className="flex items-center justify-center text-gray-400 hover:text-cyan-400 text-xs w-3 flex-shrink-0"
                            title="BOS - What to do here?"
                        >
                            🎯
                        </button>
                        <button
                            onClick={() => setShowTaskInputHelp(!showTaskInputHelp)}
                            className="flex items-center justify-center text-gray-400 hover:text-cyan-400 text-xs w-3 flex-shrink-0"
                            title="BOS - What to do here?"
                        >
                            🔷
                        </button>
                        <button
                            onClick={() => setShowADHDHelp(!showADHDHelp)}
                            className="flex items-center justify-center text-gray-400 hover:text-cyan-400 text-xs w-3 flex-shrink-0"
                            title="Attention BOS / ADHD - What to do here?"
                        >
                            🧩
                        </button>
                        <button
                            onClick={() => setShowIKIGAIHelp(!showIKIGAIHelp)}
                            className="flex items-center justify-center text-gray-400 hover:text-cyan-400 text-xs w-3 flex-shrink-0"
                            title="IKIGAI - What to do here?"
                        >
                            ✨
                        </button>
                    </div>
                </div>
                {showWhatShouldIDo && (
                    <div className="mt-2 bg-gradient-to-br from-cyan-500/10 to-blue-500/10 border border-cyan-500/20 rounded-xl p-3 text-xs">
                        <div className="font-bold text-cyan-300 mb-2">
                            🎯 What Should I Do?
                        </div>

                        <div className="space-y-2 text-gray-200 leading-relaxed">
                            <p className='text-[11px] text-xs text-gray-100'><span className="text-[11px] lg:text-xs font-semibold text-white">Choose only 1–3 important tasks</span> that would make today feel successful.</p>
                            <p className='text-[11px] text-xs text-gray-100'>
                                ✅ Make each task <span className="text-cyan-300">specific and actionable</span>.
                                Instead of <span className="italic">"Study"</span>, write
                                <span className="italic"> "Complete Chapter 5 Notes"</span>.
                            </p>
                            <p className='text-[11px] text-xs text-gray-100'>🎯 Select the objective that best matches your intention for this task.</p>
                            <p className='text-[11px] text-xs text-gray-100'>🏷️ Add tags only if they'll help you search, group, or track this task later.</p>

                            <div className="mt-3 text-[11px] lg:text-xs bg-cyan-500/10 border border-cyan-500/20 rounded-lg p-2 text-cyan-200 font-medium">
                                🚀 Progress beats perfection. Completing 3 meaningful tasks every day is more powerful than starting 20 and finishing none.
                            </div>
                        </div>
                        <div className="flex flex-col w-full gap-2">
                            <button
                            onClick={toggleGenAIHelp}
                            className={`w-fit px-2 py-1 rounded-sm text-[10px] font-bold transition-all ${
                                enableGenAIHelp
                                ? "bg-purple-500 text-white"
                                : "bg-[#111827] text-purple-300 border border-purple-500/30"
                            }`}
                            >
                            {enableGenAIHelp ? "🙈 Hide GenAI" : "🤖 Ask GenAI"}
                            </button>

                            {enableGenAIHelp && (
                                <div className="w-full bg-[#111827] rounded-2xl border border-purple-500/20 p-2">
                                    <AILightComponent
                                        mode="daily_smart_to_do"
                                        param1={"🎯 Smart Daily Task Planning Coach"}
                                        param2={`
    You are Smart Daily Productivity Coach.
    The user is planning today's most important tasks.
    Today's Goal:Plan only 1-3 meaningful tasks for my selected Objective ${objective}
    Your objective is to give me how to choose high-impact tasks that build consistency, identity and long-term success.

    Maximum Daily Tasks:3
    Purpose: Small daily wins compound into identity change.


    DO NOT invent new objectives.

    When responding, provide:

    1. 🧠 Why this objective fits
    (1-2 short sentences.)

    2. 🚀 Expected Benefit
    (What completing task under this objective helps improve.)

    3. 🔄 GrowthLoop & IKIGAI
    If this ${objective} fits into GrowthLoop (ADHD and other Children or even adult behavior) then explain in 1-2 short sentences how consistently completing tasks under the objective helps.
    If this ${objective} contributes to one or more dimensions of IKIGAI (what you love, what you're good at, what the world needs, or what creates value for your future) then briefly explain how.
    Keep both explanations simple, practical and motivating to be created as ToDO task.

    Keep the response under 120 words.

    Help the user understand:
    • Why creating only 1-3 important tasks is better than creating many.
    • How to identify today's Highest Impact Task.
    • How to write a task that is Specific, Clear and Actionable.
    • How to choose the most appropriate Objective category.
    • When tags are useful and when they are unnecessary.
    • Common mistakes people make while creating daily tasks.

    Show 3 examples of: ❌ Weak Tasks ✅ Better Tasks
    Examples should be practical for students, professionals and entrepreneurs.

    Finish with:

    🚀 Today's Success Formula
    Provide 5 short productivity rules that can be followed today.
    End with one powerful motivational sentence that inspires action without sounding cheesy.
    Keep the response under 220 words.
    Use headings, emojis and bullet points for easy reading.
    Maintain a friendly, encouraging and Gen-Z friendly tone.
    `}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                )}                
                {showTaskInputHelp && (
                    <PDBOSHelp />
                )}
                {showADHDHelp && (
                    <PDADHDHelp />
                )}
                {showIKIGAIHelp && (
                    <PDIKIGAIHelp />
                )}

                <input
                type="date"
                value={todoDate || ""}
                min={normalizedStart || undefined}
                max={normalizedEnd || undefined}
                onChange={(e) => setTodoDate(e.target.value)}
                disabled={isOutOfRange}
                className="w-full bg-[#111827] border border-gray-600 rounded-xl p-3 text-sm text-white 
                        focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 outline-none
                        date-picker-custom disabled:opacity-40 disabled:cursor-not-allowed"
            />
                <input
                    className="w-full mt-1 px-2 py-1 bg-gray-700 text-white rounded-sm text-xs border border-cyan-100"
                    placeholder="What's the ONE task that would make today successful?"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                />

                <p className="text-xs text-gray-50 mt-2">🎯 Select Objective of the above task</p>
                <div className="grid grid-cols-3 lg:grid-cols-6 gap-1 mt-2">
                    {[
                        { emoji: "🎓", label: "Learning" },
                        { emoji: "💼", label: "Career" },
                        { emoji: "🧠", label: "Discipline" },
                        { emoji: "💪", label: "Health" },
                        { emoji: "⚽", label: "Sports" },
                        { emoji: "🚀", label: "Build" },
                        { emoji: "📈", label: "Productivity" },
                        { emoji: "🎯", label: "Focus" },
                        { emoji: "🌱", label: "Growth" },

                        { emoji: "🧠", label: "Attention" },
                        { emoji: "❤️", label: "Connection" },
                        { emoji: "😊", label: "Emotional Regulation" },
                        { emoji: "📋", label: "Routine" },
                        { emoji: "🏃", label: "Movement" },
                        { emoji: "😴", label: "Sleep" },
                        { emoji: "🌟", label: "Confidence" },
                        { emoji: "🎬", label: "Entertainment" },

                        { emoji: "🧘", label: "Mindfulness" },
                        { emoji: "🎨", label: "Creativity" },
                        { emoji: "🤝", label: "Relationships" },
                        { emoji: "🚀", label: "Entrepreneurship" },
                        { emoji: "💰", label: "Financial" },
                        { emoji: "👑", label: "Leadership" },

                        { emoji: "🎲", label: "Random" }
                    ].map((objectiveButtons) => (
                         <button
                            key={objectiveButtons.label}
                            onClick={() => setObjective(objectiveButtons.label)}
                            className={`px-2 py-1 rounded-sm text-[10px] border transition-all
                            ${
                                objective === objectiveButtons.label
                                ? "bg-cyan-700 text-gray-100 border-cyan-400"
                                : "bg-[#111827] text-gray-100 border-gray-700"
                            }
                            `}
                        >
                            <div className='gap-2'>{objectiveButtons.emoji}{objectiveButtons.label}</div>
                        </button>
                    ))}
                </div>
                <div className="flex flex-col space-y-1 py-1">
                    <div className="flex items-center justify-between">
                        <p className="text-xs text-gray-200 mt-3">
                            Optional: Add tags for easier tracking
                        </p>
                        <button
                            onClick={() => setShowTagHelp(!showTagHelp)}
                            className="w-6 text-gray-400 hover:text-cyan-400 text-xs"
                            title="What are tags?"
                        >
                            ❔
                        </button>
                    </div>
                    <input
                        className="w-full mt-1 px-2 py-1 bg-gray-700 text-white rounded-lg text-xs border border-gray-400"
                        placeholder="Input tag(s) (comma separated)?"
                        value={tags}
                        onChange={(e) => setTags(e.target.value)}
                    />
                </div>
                {showTagHelp && (
                    <div className="flex flex-col p-3 bg-gray-800 text-gray-200 text-xs rounded-lg border border-gray-700 space-y-2">
                        <p className="font-semibold text-xs text-cyan-300">💡 What you can put in tags</p>
                        <p className='text-xs text-gray-100'>Use simple comma-separated labels to help you search & filter later.</p>
                        <div className="space-y-1 ">
                            <p className='text-xs text-gray-100'>👤 <b>Identity:</b> Name, Person, Client Name</p>
                            <p className='text-xs text-gray-100'>📍 <b>Location:</b> City, Area, Pincode</p>
                            <p className='text-xs text-gray-100'>📞 <b>Contact:</b> Mobile Number, Email</p>
                            <p className='text-xs text-gray-100'>💼 <b>Work:</b> Project, Task Type, Client</p>
                            <p className='text-xs text-gray-100'>💰 <b>Finance:</b> Expense, Income, Salary, GST</p>
                            <p className='text-xs text-gray-100'>🏷️ <b>Category:</b> Urgent, Personal, Business</p>
                            <p className='text-xs text-gray-100'>📦 <b>Other:</b> Anything you want to group or track</p>
                        </div>

                        <p className="text-gray-400 mt-2">
                            Example: <span className="text-white">Abhishek, Mumbai, GST, ClientWork</span>
                        </p>
                    </div>
                )}
                {message.text && (
                    <div className={`rounded-2xl border px-4 py-2 text-xs lg:text-sm font-medium flex items-center gap-3 shadow-lg ${
                    message.type === 'success' 
                        ? 'bg-green-900/50 border-green-500 text-green-200' 
                        : 'bg-red-900/50 border-red-500 text-red-200'
                    }`}>
                    <i className={`fa-solid ${message.type === 'success' ? 'fa-check-circle' : 'fa-exclamation-triangle'}`}></i>
                    <span>{message.text}</span>
                    <button 
                        onClick={() => setMessage({ text: '', type: '' })}
                        className="w-fit ml-auto text-lg px-2 py-1 bg-blue-600 text-white leading-none hover:text-white"
                    >
                        ×
                    </button>
                    </div>
                )}
                {/* <button
                    disabled={dailySubmitted || !isActiveSub || todayTodoCount >= MAX_DAILY_TODOS}
                    onClick={handleCreateTodo}
                    className={`w-full font-bold mt-4 py-3 rounded-2xl text-xs transition-all
                        ${isSubmitting
                            ? "bg-gray-500 cursor-not-allowed"
                            : "bg-green-500 hover:bg-green-400 active:scale-[0.98]"
                        }
                    `}
                >
                    🚀 Submit Daily Task {isActiveSub ? "" : "Inactive Subscription"}
                </button> */}

                {todayTodoCount >= MAX_DAILY_TODOS && (
                    <div className="mt-4 mb-2 rounded-2xl border border-pink-500/40 bg-gradient-to-r from-pink-500/20 via-purple-500/20 to-cyan-500/20 p-4 text-center animate-pulse">
                        <div className="text-lg font-black">
                            🎉 QUEST COMPLETE!
                        </div>

                        <div className="mt-1 text-sm text-pink-200 font-semibold">
                            You crushed today's {MAX_DAILY_TODOS} missions 💪
                        </div>

                        <div className="text-xs text-gray-300 mt-2">
                            Time to recharge ⚡ and unlock fresh quests tomorrow 🚀
                        </div>
                    </div>
                )}
                <button
                    disabled={dailySubmitted || !isActiveSub || todayTodoCount >= MAX_DAILY_TODOS}
                    onClick={handleCreateTodo}
                    className={`w-fit font-bold mt-4 px-2 py-1 rounded-lg text-xs transition-all
                        ${
                            isSubmitting
                                ? "bg-gray-500 cursor-not-allowed"
                                : todayTodoCount >= MAX_DAILY_TODOS
                                ? "bg-gray-600 cursor-not-allowed"
                                : "bg-green-500 hover:bg-green-400 active:scale-[0.98]"
                        }
                    `}
                >
                    {todayTodoCount >= MAX_DAILY_TODOS
                        ? "🚫 Daily Quest Limit Reached"
                        : `🚀 Submit Daily Task ${
                            isActiveSub ? `(${todayTodoCount}/${MAX_DAILY_TODOS})` : "Inactive Subscription"
                        }`}
                </button>

                {dailySubmitted && (
                    <div className="mt-4 bg-gradient-to-br from-cyan-500/10 to-blue-500/10 border border-cyan-500/20 rounded-2xl p-4">
                        <div className="flex items-start gap-3">
                            <div className="text-3xl">
                                🎉
                            </div>
                            <div className="flex-1">
                                <div className="text-sm font-bold text-cyan-300">
                                    Daily Mission Submitted
                                </div>
                                <div className="text-xs text-gray-300 mt-2 leading-relaxed">
                                    Wow! You completed your daily task check-in.
                                    Keep stacking consistency to unlock hidden rewards,
                                    streak bonuses & surprise gifts 👀
                                </div>
                                {!showLuckyDraw && (
                                    <button
                                        onClick={() => setShowLuckyDraw(true)}
                                        disabled
                                        className="mt-4 w-full bg-cyan-500 hover:bg-cyan-400 text-black py-2 rounded-xl text-xs font-bold transition-all"
                                    >
                                        🎁 Check Surprise Lucky Winner Gift
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                )}
                {showLuckyDraw && (
                    <div className="mt-4">
                        {hasWonGift ? (
                            <div className="bg-gradient-to-br from-yellow-500/20 to-orange-500/20 border border-yellow-400/30 rounded-2xl p-5">
                                <div className="text-center">
                                    <div className="text-5xl animate-bounce">
                                        🎁
                                    </div>
                                    <div className="text-xl font-black text-yellow-300 mt-3">
                                        YOU WON A SURPRISE GIFT!
                                    </div>
                                    <div className="text-xs text-gray-200 mt-3 leading-relaxed">
                                        Congratulations 🚀
                                        Your consistency unlocked a surprise reward.
                                        <br /><br />
                                        Claim it now to receive your gift at home.
                                    </div>
                                    {!giftClaimed ? (
                                        <button
                                            onClick={() => setGiftClaimed(true)}
                                            className="mt-5 w-full bg-yellow-400 hover:bg-yellow-300 text-black py-2 rounded-2xl text-sm font-black transition-all"
                                        >
                                            🎉 Claim My Gift
                                        </button>
                                    ) : (
                                        <div className="mt-5 bg-[#111827] rounded-2xl p-4 border border-green-500/20">
                                            <div className="text-green-400 font-bold text-sm">
                                                ✅ Claim Registered Successfully
                                            </div>
                                            <div className="text-xs text-gray-300 mt-2 leading-relaxed">
                                                Our vendor will send your surprise gift soon 🚚
                                                <br /><br />
                                                Please keep your address updated. No payment is required.
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div className="bg-gradient-to-br from-[#111827] to-[#1f2937] border border-gray-700 rounded-2xl p-5">
                                <div className="text-center">
                                    <div className="text-5xl">
                                        😅
                                    </div>
                                    <div className="text-lg font-bold text-white mt-3">
                                        Almost There!
                                    </div>
                                    <div className="text-xs text-gray-300 mt-3 leading-relaxed">
                                        No surprise gift today...
                                        <br /><br />
                                        BUT your consistency score is increasing 📈
                                        <br /><br />
                                        Keep posting daily tasks to unlock future rewards,
                                        streak boosts & hidden drops 🎁
                                    </div>
                                    <div className="mt-4 inline-flex items-center gap-2 bg-cyan-500/10 text-cyan-300 px-4 py-1 rounded-full text-[11px] font-bold border border-cyan-500/20">
                                        🔥 Consistency = Higher Winning Chances
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default PDDaily;